/**
 * SCR-005 — Branches list (admin only).
 * Table on md+, card list on mobile. Search + status filter + pagination.
 * Create/edit via Dialog modal. Status badge color-coded.
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { type ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { MoreHorizontal, Pencil, Plus, XCircle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@client/components/data-table';
import { BranchForm, type BranchFormValues } from '@client/components/forms/branch-form';
import {
  type Branch,
  branchKeys,
  isApiError,
  useBranches,
  useCreateBranch,
  useUpdateBranch,
} from '@client/lib/queries/use-branches';
import { useQueryClient } from '@tanstack/react-query';

export const Route = createFileRoute('/admin/branches/')({
  component: BranchesListPage,
});

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  active: { label: 'Hoạt động', className: 'bg-brand-green-100 text-brand-green-700' },
  inactive: { label: 'Tạm dừng', className: 'bg-secondary text-muted-foreground' },
  closed: { label: 'Đã đóng', className: 'bg-destructive/10 text-destructive' },
};

function BranchesListPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Branch | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [blockingClasses, setBlockingClasses] = useState<{ code: string; name: string }[]>([]);

  const params = {
    page,
    pageSize: PAGE_SIZE,
    search: search || undefined,
    status: statusFilter === 'all' ? undefined : statusFilter,
  };

  const { data, isLoading, isError, refetch } = useBranches(params);
  const createBranch = useCreateBranch();
  const updateBranch = useUpdateBranch(editTarget?.id ?? '');

  function openCreate() {
    setEditTarget(null);
    setFormError(null);
    setBlockingClasses([]);
    setDialogOpen(true);
  }

  function openEdit(branch: Branch) {
    setEditTarget(branch);
    setFormError(null);
    setBlockingClasses([]);
    setDialogOpen(true);
  }

  async function handleSubmit(values: BranchFormValues) {
    setFormError(null);
    setBlockingClasses([]);
    try {
      if (editTarget) {
        await updateBranch.mutateAsync(values);
        toast.success('Đã cập nhật cơ sở.');
      } else {
        await createBranch.mutateAsync(values);
        toast.success('Đã tạo cơ sở mới.');
      }
      setDialogOpen(false);
      void qc.invalidateQueries({ queryKey: branchKeys.all });
    } catch (err) {
      if (isApiError(err)) {
        if (err.status === 409) {
          setFormError('Mã cơ sở đã tồn tại.');
        } else if (err.status === 422) {
          const body = err.body as { message?: string; blockingClasses?: { code: string; name: string }[] };
          setFormError(body?.message ?? 'Không thể thực hiện thao tác này.');
          setBlockingClasses(body?.blockingClasses ?? []);
        } else {
          setFormError(err.message);
        }
      } else {
        setFormError('Không kết nối được máy chủ, thử lại sau.');
      }
    }
  }

  const isSubmitting = createBranch.isPending || updateBranch.isPending;

  const columns: ColumnDef<Branch>[] = [
    {
      accessorKey: 'code',
      header: 'Mã',
      cell: ({ row }) => (
        <Link
          to="/admin/branches/$branchId"
          params={{ branchId: row.original.id }}
          className="font-mono text-sm text-primary hover:underline"
        >
          {row.original.code}
        </Link>
      ),
    },
    {
      accessorKey: 'name',
      header: 'Tên cơ sở',
      cell: ({ row }) => (
        <Link
          to="/admin/branches/$branchId"
          params={{ branchId: row.original.id }}
          className="hover:underline"
        >
          {row.original.name}
        </Link>
      ),
    },
    {
      accessorKey: 'phone',
      header: 'SĐT',
      cell: ({ row }) => row.original.phone ?? '—',
    },
    {
      accessorKey: 'openingDate',
      header: 'Khai trương',
      cell: ({ row }) =>
        row.original.openingDate
          ? format(new Date(row.original.openingDate), 'dd/MM/yyyy')
          : '—',
    },
    {
      accessorKey: 'status',
      header: 'Trạng thái',
      cell: ({ row }) => {
        const s = STATUS_LABELS[row.original.status] ?? STATUS_LABELS['active'];
        return (
          <Badge className={`${s?.className ?? ''} border-0 text-xs`}>{s?.label}</Badge>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => openEdit(row.original)}>
              <Pencil className="h-4 w-4 mr-2" /> Sửa
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => {
                openEdit({ ...row.original, status: 'closed' });
              }}
            >
              <XCircle className="h-4 w-4 mr-2" /> Đóng cơ sở
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-foreground">Cơ sở</h1>
        <Button
          className="bg-accent text-accent-foreground hover:bg-accent/90 h-10 self-start sm:self-auto"
          onClick={openCreate}
        >
          <Plus className="h-4 w-4 mr-1.5" /> Thêm cơ sở
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        isLoading={isLoading}
        isError={isError}
        onRetry={() => void refetch()}
        emptyMessage="Chưa có cơ sở nào."
        searchPlaceholder="Tìm theo tên/mã..."
        searchValue={search}
        onSearchChange={(v) => { setSearch(v); setPage(1); }}
        pagination={
          data
            ? { page, pageSize: PAGE_SIZE, total: data.total }
            : undefined
        }
        onPageChange={setPage}
        filterSlot={
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="h-10 w-[140px]">
              <SelectValue placeholder="Trạng thái" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tất cả</SelectItem>
              <SelectItem value="active">Hoạt động</SelectItem>
              <SelectItem value="inactive">Tạm dừng</SelectItem>
              <SelectItem value="closed">Đã đóng</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editTarget ? 'Sửa cơ sở' : 'Thêm cơ sở'}</DialogTitle>
          </DialogHeader>

          {formError && (
            <Alert variant="destructive" className="mt-2">
              <AlertDescription>
                {formError}
                {blockingClasses.length > 0 && (
                  <ul className="mt-2 list-disc pl-4 text-xs space-y-0.5">
                    {blockingClasses.map((c) => (
                      <li key={c.code}>
                        <span className="font-mono">{c.code}</span> — {c.name}
                      </li>
                    ))}
                  </ul>
                )}
              </AlertDescription>
            </Alert>
          )}

          <BranchForm
            isEdit={!!editTarget}
            isSubmitting={isSubmitting}
            defaultValues={
              editTarget
                ? {
                    code: editTarget.code,
                    name: editTarget.name,
                    address: editTarget.address,
                    phone: editTarget.phone ?? '',
                    email: editTarget.email ?? '',
                    status: editTarget.status,
                    openingDate: editTarget.openingDate ?? '',
                  }
                : undefined
            }
            onSubmit={handleSubmit}
            onCancel={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
